import { useI18n } from 'vue-i18n'
import { ToastType } from '~/enums'
import type { NotificationInboxItem } from '~/services/api'
import { useApiClient } from '~/services/api'
import { useLogger } from '~/composables/useLogger'
import { resetAccountNotifications } from '~/composables/resetAccountNotifications'
import { useIdentity } from '~/composables/useIdentity'
import { useNotificationsInbox } from '~/composables/useNotificationsInbox'
import { useNotificationsRealtime } from '~/composables/useNotificationsRealtime'
import { Normalization } from '~/utils/normalization'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { resolveProblemMessageWithReference } from '~/services/api/resolveProblemMessageWithReference'

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const useNotifications = () => {
  const logger = useLogger('useNotifications')
  const notificationsStore = useNotificationsStore()
  const { notifications: notificationsApi } = useApiClient()
  const { show } = useToast()
  const { isAuthenticated, identity } = useIdentity()
  const { t } = useI18n()
  const nuxtApp = useNuxtApp()

  const broadcastChannelIdentifier = computed(() =>
    Normalization.stringValue(identity.value?.broadcast_channel_identifier, null)
  )

  const normalizeNotificationIds = (notificationIds: string[]) => {
    return [
      ...new Set(notificationIds.map((id) => id.trim()).filter((id) => UUID_PATTERN.test(id)))
    ]
  }

  const { loadInitial, loadMore } = useNotificationsInbox(isAuthenticated)
  const markAsRead = async (notificationId: string) => {
    await notificationsApi().markAsRead(notificationId)
    notificationsStore.markAsReadLocally(notificationId)
  }

  const markAllAsRead = async () => {
    try {
      await notificationsApi().markAllAsRead()
      notificationsStore.markAllAsReadLocally()
    } catch (error) {
      logger.error('markAllAsRead', error)
      show(
        ToastType.ERROR,
        resolveProblemMessageWithReference(
          AppProblemModel.normalize(error),
          t,
          t('dashboard.messages.couldNotMarkAllNotificationsRead')
        )
      )
    }
  }

  const deleteNotifications = async (notificationIds: string[]) => {
    const validIds = normalizeNotificationIds(notificationIds)

    if (!validIds.length) {
      return null
    }

    const response = await notificationsApi().deleteFromInbox({
      notifications: validIds
    })

    notificationsStore.removeItems(validIds)
    notificationsStore.setUnreadCount(response.data.unread_count)

    return response
  }

  const openNotification = async (notification: NotificationInboxItem) => {
    if (!notification.is_read) {
      try {
        await markAsRead(notification.id)
      } catch (error) {
        logger.error('openNotification', { step: 'markAsRead', error })
        show(
          ToastType.ERROR,
          resolveProblemMessageWithReference(
            AppProblemModel.normalize(error),
            t,
            t('dashboard.messages.couldNotMarkNotificationRead')
          )
        )
      }
    }

    notificationsStore.setPanelOpen(false)

    if (notification.action_url != null && notification.action_url !== '') {
      await navigateTo(notification.action_url)
    }
  }

  const { subscribe, unsubscribe } = useNotificationsRealtime({
    isAuthenticated,
    broadcastChannelIdentifier,
    openNotification
  })

  // Clears in-memory state first; transport teardown is best-effort so a throwing step can't leave notifications populated.
  const reset = async () => {
    try {
      resetAccountNotifications(nuxtApp)
    } catch (error: unknown) {
      logger.error('reset', { step: 'disconnect', error })
    }

    try {
      await unsubscribe()
    } catch (error: unknown) {
      logger.error('reset', { step: 'unsubscribe', error })
    }
  }

  return {
    notificationsStore,
    loadInitial,
    loadMore,
    subscribe,
    unsubscribe,
    markAsRead,
    markAllAsRead,
    deleteNotifications,
    openNotification,
    reset
  }
}
